import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
} from '@angular/core';

import { ButtonModule, IconModule } from '@hglobal/common';
import { TranslateModule } from '@ngx-translate/core';
import { environment } from '@fb/environments';

import { ChatMessageComponent } from '../chat-message/chat-message.component';
import {
  ChatMessage,
  ChatMessageReplyTypes,
  ChatMessageStatus,
  InternalMessageStatus,
} from '@fb/models';
import { MessageDatePipe } from '@fb/pipes/date.pipe';
import { SlackLinkDirective } from '@fb/directives/slack-link.directive';
import { AiCharStore } from '../../store';
import { MessageReactionComponent } from '../message-reaction/message-reaction.component';
import { TranslateInPipe } from '@fb/pipes/translate-in.pipe';
import { ParseMarkdownPipe } from '@fb/pipes/parse-markdown.pipe';
import { SafeHtmlPipe } from '@fb/pipes/safe-html.pipe';

@Component({
  selector: 'bot-chat-message',
  templateUrl: './bot-chat-message.component.html',
  styleUrl: './bot-chat-message.component.scss',
  imports: [
    ChatMessageComponent,
    ButtonModule,
    MessageDatePipe,
    SlackLinkDirective,
    IconModule,
    TranslateModule,
    MessageReactionComponent,
    TranslateInPipe,
    ParseMarkdownPipe,
    SafeHtmlPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BotChatMessageComponent {
  #aiChatStore = inject(AiCharStore);

  message = input.required<ChatMessage>();
  isFirst = input<boolean>(false);
  chatStarted = input<string>(null);

  text = computed(() => this.message().reply);
  links = computed(() => this.message().links || []);
  chatStartedAfter = computed(
    () => this.chatStarted() && this.message().createdAt < this.chatStarted(),
  );

  // These types must render the backend `reply` as-is (no FE templates)
  readonly rawReplyTypes: ChatMessageReplyTypes[] = [
    ChatMessageReplyTypes.SIMPLE,
    ChatMessageReplyTypes.JIRA_TASK,
    ChatMessageReplyTypes.JIRA_READY_TO_CREATE,

    ChatMessageReplyTypes.VACATION_BALANCE,
    ChatMessageReplyTypes.TOTAL_BALANCE,
    ChatMessageReplyTypes.SICK_LEAVE_BALANCE,
    ChatMessageReplyTypes.SICK_LEAVE_80_BALANCE,
    ChatMessageReplyTypes.DAY_OFF_BALANCE,
    ChatMessageReplyTypes.RECOVERY_BALANCE,
    ChatMessageReplyTypes.FAMILY_BALANCE,
  ];

  showRawReply = computed(() =>
    this.rawReplyTypes.includes(this.message().replyType)
  );

  balances = computed(() => {
    const obj: Partial<Record<ChatMessageReplyTypes, number>> = {};

    this.message().timeOffBalances.forEach((timeOffBalance) => {
      obj[timeOffBalance.type] = timeOffBalance.value;
    });

    return obj;
  });

  displayReaction = computed(
    () =>
      this.isFirst() &&
      this.message().internalStatus === InternalMessageStatus.resolved &&
      this.message().status === ChatMessageStatus.Delivered &&
      this.message().isSuccessReply &&
      this.message().request,
  );

  internalMessageStatus = InternalMessageStatus;
  chatMessageStatus = ChatMessageStatus;
  chatMessageReplyTypes = ChatMessageReplyTypes;

  aiChatBotIcon = `${environment.shortName}-ai-chat-bot`;

  originLink = window.location.origin;

  tryAgain() {
    this.#aiChatStore.sendQuestion(this.message());
  }

  randomRephrase(dateStr: string): number {
    const date = new Date(dateStr);
    const milliseconds = date.getMilliseconds();

    return milliseconds % 10;
  }
}
